import React, { useEffect, useState } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';

import {
  FaUsers,
  FaBuilding,
  FaUserFriends,
  FaHome,
  FaDoorClosed,
  FaTools,
  FaMoneyBillWave,
  FaMedal,
  FaWallet,
  FaBalanceScale,
  FaChartLine,
  FaFilePdf,
  FaShieldAlt,
  FaChartBar
} from 'react-icons/fa';

const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ec4899'];

const DashboardHome = () => {
  const [barData, setBarData] = useState([]);
  const [pieData, setPieData] = useState([]);
  const [topMonths, setTopMonths] = useState([]);
  const [topUnits, setTopUnits] = useState([]);
  const [leastUnits, setLeastUnits] = useState([]);
  const [stats, setStats] = useState({
    totalTenants: 0,
    apartmentUnits: 0,
    totalPeople: 0,
    availableUnits: 0,
    unavailableUnits: 0,
    underMaintenanceUnits: 0,
    totalRevenue: 0,
    totalExpenses: 0,
    averageMonthlyIncome: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tenantsResponse, unitsResponse, expensesResponse] = await Promise.all([
          axios.get('https://ikaze-thouse-backend.vercel.app/api/v1/tenants/getAll'),
          axios.get('https://ikaze-thouse-backend.vercel.app/api/v1/appartmentUnits/getAll'),
          axios.get('https://ikaze-thouse-backend.vercel.app/api/v1/expenses/getAll')
        ]);

        const tenants = tenantsResponse.data.data || [];
        const totalTenants = tenantsResponse.data.totalTenants || tenants.length;
        const apartmentUnitsList = unitsResponse.data.data || [];
        const apartmentUnits = apartmentUnitsList.length;

        const expensesList = expensesResponse.data.success ? expensesResponse.data.data : [];
        let totalExpenses = expensesList.reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0);

        const availableUnits = apartmentUnitsList.filter(
          unit => unit.status?.toLowerCase() === 'available'
        ).length;
        
        const underMaintenanceUnits = apartmentUnitsList.filter(
          unit => unit.status?.toLowerCase() === 'maintenance' || unit.status?.toLowerCase() === 'under maintenance'
        ).length;
        
        const occupiedUnits = apartmentUnitsList.filter(
          unit => unit.status?.toLowerCase() === 'occupied' || unit.status?.toLowerCase() === 'unavailable'
        ).length;
        
        const finalOccupiedUnits = occupiedUnits > 0 ? occupiedUnits : (apartmentUnits - availableUnits - underMaintenanceUnits);

        const grouped = {};
        apartmentUnitsList.forEach(unit => {
          const name = unit.name || unit.unit_number || unit.apartment_name;
          if (name) {
            grouped[name] = { name: String(name), Rent: 0 };
          }
        });

        tenants.forEach(tenant => {
          const unitName = tenant.appartment_unite;
          if (unitName) {
            const stringUnitName = String(unitName);
            if (!grouped[stringUnitName]) {
              grouped[stringUnitName] = { name: stringUnitName, Rent: 0 };
            }
            grouped[stringUnitName].Rent += Number(tenant.rent_rwf) || 0;
          }
        });

        const totalPeople = tenants.reduce((total, tenant) => {
          return total + (Number(tenant.number_adults) || 0) + (Number(tenant.number_child) || 0);
        }, 0);

        let totalRevenue = tenants.reduce((sum, t) => sum + (Number(t.rent_rwf) || 0), 0);

        const currentYear = new Date().getFullYear();
        const monthlyIncomeMap = {};
        
        tenants.forEach(t => {
          const dateField = t.createdAt || t.arrival_date || t.date;
          if (dateField) {
            const date = new Date(dateField);
            if (!isNaN(date.getTime()) && date.getFullYear() === currentYear) {
              const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
              monthlyIncomeMap[monthKey] = (monthlyIncomeMap[monthKey] || 0) + (Number(t.rent_rwf) || 0);
            }
          }
        });

        const currentMonthIndex = new Date().getMonth();
        const activeMonthsCount = Math.max(currentMonthIndex + 1, 1);
        let averageMonthlyIncome = totalRevenue / activeMonthsCount;

        const sortedMonths = Object.entries(monthlyIncomeMap)
          .map(([monthKey, income]) => {
            const [y, m] = monthKey.split('-');
            const monthName = new Date(y, m).toLocaleString('default', { month: 'short', year: 'numeric' });
            return { month: monthName, income };
          })
          .sort((a, b) => b.income - a.income)
          .slice(0, 5);
        setTopMonths(sortedMonths);

        const allMappedUnits = Object.values(grouped).map(unit => ({ name: unit.name, income: unit.Rent }));

        const sortedUnits = [...allMappedUnits].sort((a, b) => b.income - a.income).slice(0, 3);
        setTopUnits(sortedUnits);

        const sortedLeastUnits = [...allMappedUnits].sort((a, b) => a.income - b.income).slice(0, 3);
        setLeastUnits(sortedLeastUnits);

        setBarData(Object.values(grouped));
        setPieData([
          { name: 'Occupied', value: finalOccupiedUnits },
          { name: 'Available', value: availableUnits },
          { name: 'Maintenance', value: underMaintenanceUnits },
        ]);

        setStats({
          totalTenants,
          apartmentUnits,
          totalPeople,
          availableUnits,
          unavailableUnits: finalOccupiedUnits,
          underMaintenanceUnits,
          totalRevenue,
          totalExpenses,
          averageMonthlyIncome,
        });
      } catch (error) {
        console.error("Error fetching dashboard data from APIs:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const netProfit = stats.totalRevenue - stats.totalExpenses;

  const generatePDFReport = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFillColor(15, 23, 42);
    doc.rect(0, 0, pageWidth, 36, 'F');
    
    doc.setFillColor(99, 102, 241);
    doc.rect(0, 34, pageWidth, 2, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text('LaSplendide Apartment', 14, 16);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(148, 163, 184);
    doc.text('Executive Business Intelligence & Operational Report', 14, 25);

    doc.setTextColor(100, 116, 139);
    doc.setFontSize(8.5);
    doc.text(`Generated On: ${new Date().toLocaleDateString()} | Scope: Comprehensive Property Analysis`, 14, 46);

    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(226, 232, 240);
    doc.roundedRect(14, 52, pageWidth - 28, 20, 2, 2, 'FD');
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(30, 41, 59);
    doc.text('PERFORMANCE OVERVIEW:', 18, 60);
    
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(71, 85, 105);
    doc.text(`Operating status remains steady with net returns at ${netProfit.toLocaleString()} RWF across ${stats.apartmentUnits} management units.`, 18, 67);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10.5);
    doc.setTextColor(15, 23, 42);
    doc.text('1. Financial Breakdown & Revenue Matrix', 14, 86);

    autoTable(doc, {
      startY: 90,
      head: [['Financial Category', 'Recorded Value (RWF)', 'Status']],
      body: [
        ['Total Gross Revenue', `${stats.totalRevenue.toLocaleString()} RWF`, 'Verified YTD'],
        ['Total Operating Expenses', `${stats.totalExpenses.toLocaleString()} RWF`, 'Outflows'],
        ['Net Operating Profit / Loss', `${netProfit.toLocaleString()} RWF`, netProfit >= 0 ? 'Profit' : 'Loss'],
        ['Average Monthly Inflow', `${Math.round(stats.averageMonthlyIncome).toLocaleString()} RWF`, 'Average'],
      ],
      theme: 'striped',
      headStyles: { fillColor: [79, 70, 229], fontSize: 9, fontStyle: 'bold' },
      styles: { fontSize: 8.5, cellPadding: 5 }
    });

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10.5);
    doc.setTextColor(15, 23, 42);
    doc.text('2. Tenant Demographics & Asset Tracking', 14, doc.lastAutoTable.finalY + 12);

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 16,
      head: [['Operational Indicator', 'Total Volume']],
      body: [
        ['Registered Tenant Accounts', stats.totalTenants],
        ['Total Apartment Units Managed', stats.apartmentUnits],
        ['Aggregate Resident Count (Adults & Children)', stats.totalPeople],
        ['Vacant / Available Units', stats.availableUnits],
        ['Occupied Units', stats.unavailableUnits],
        ['Units Under Scheduled Maintenance', stats.underMaintenanceUnits],
      ],
      theme: 'striped',
      headStyles: { fillColor: [16, 185, 129], fontSize: 9, fontStyle: 'bold' },
      styles: { fontSize: 8.5, cellPadding: 4.5 }
    });

    const rankingRows = [];
    const maxLength = Math.max(topUnits.length, leastUnits.length);
    for (let i = 0; i < maxLength; i++) {
      rankingRows.push([
        topUnits[i] ? `#${i + 1} Unit ${topUnits[i].name}` : '',
        topUnits[i] ? `${topUnits[i].income.toLocaleString()} RWF` : '',
        leastUnits[i] ? `#${i + 1} Unit ${leastUnits[i].name}` : '',
        leastUnits[i] ? `${leastUnits[i].income.toLocaleString()} RWF` : ''
      ]);
    }

    if (rankingRows.length > 0) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10.5);
      doc.setTextColor(15, 23, 42);
      doc.text('3. Unit Revenue Performance Tiers', 14, doc.lastAutoTable.finalY + 12);

      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 16,
        head: [['Top Performing Units', 'Revenue', 'Lowest Performing Units', 'Revenue']],
        body: rankingRows,
        theme: 'striped',
        headStyles: { fillColor: [245, 158, 11], fontSize: 9, fontStyle: 'bold' },
        styles: { fontSize: 8.5, cellPadding: 4.5 }
      });
    }

    doc.save(`Executive_Dashboard_Report_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  if (loading) {
    return (
      <div className="dashboard-loader">
        <div className="spinner"></div>
        <h2>Loading dashboard overview...</h2>
      </div>
    );
  }

  const cardInfo = [
    { title: 'Total Tenants', value: stats.totalTenants, icon: <FaUsers />, color: 'blue' },
    { title: 'Apartment Units', value: stats.apartmentUnits, icon: <FaBuilding />, color: 'indigo' },
    { title: 'Total People', value: stats.totalPeople, icon: <FaUserFriends />, color: 'purple' },
    { title: 'Available Units', value: stats.availableUnits, icon: <FaHome />, color: 'green' },
    { title: 'Occupied Units', value: stats.unavailableUnits, icon: <FaDoorClosed />, color: 'orange' },
    { title: 'Maintenance', value: stats.underMaintenanceUnits, icon: <FaTools />, color: 'red' },
  ];

  return (
    <div className="dashboard-home">
      {/* Top Banner Header */}
      <div className="dashboard-header-flex">
        <div className="dashboard-header">
          <span className="badge-pill"><FaShieldAlt /> Management Portal</span>
          <h2>Manager Dashboard Overview</h2>
          <p>Real-time property analytics, financial flows, and resident metrics.</p>
        </div>
        <button className="btn-download-pdf" onClick={generatePDFReport}>
          <FaFilePdf /> Export Executive PDF
        </button>
      </div>

      {/* Core Indicator Cards (Rectangular Layout) */}
      <div className="dashboard-cards">
        {cardInfo.map((card, index) => (
          <div key={index} className={`dashboard-card card-${card.color}`}>
            <div className="card-content">
              <h3>{card.title}</h3>
              <p>{card.value}</p>
            </div>
            <div className="icon-wrapper">{card.icon}</div>
          </div>
        ))}
      </div>

      {/* Financial Summary Section (Displayed underneath cards in a clean non-card bar format) */}
      <div className="financial-summary-bar">
        <div className="financial-metric-item">
          <div className="financial-icon-small income"><FaMoneyBillWave /></div>
          <div className="financial-text">
            <span>Total Income</span>
            <h4>{stats.totalRevenue.toLocaleString()} RWF</h4>
          </div>
        </div>
        <div className="financial-divider"></div>
        <div className="financial-metric-item">
          <div className="financial-icon-small expenses"><FaWallet /></div>
          <div className="financial-text">
            <span>Total Expenses</span>
            <h4>{stats.totalExpenses.toLocaleString()} RWF</h4>
          </div>
        </div>
        <div className="financial-divider"></div>
        <div className="financial-metric-item">
          <div className="financial-icon-small profit"><FaBalanceScale /></div>
          <div className="financial-text">
            <span>Net Profit / Loss</span>
            <h4 className={netProfit >= 0 ? 'text-success' : 'text-danger'}>
              {netProfit.toLocaleString()} RWF
            </h4>
          </div>
        </div>
        <div className="financial-divider"></div>
        <div className="financial-metric-item">
          <div className="financial-icon-small average"><FaChartLine /></div>
          <div className="financial-text">
            <span>Avg. Income / Month</span>
            <h4>{Math.round(stats.averageMonthlyIncome).toLocaleString()} RWF</h4>
          </div>
        </div>
      </div>

      {/* Rankings Section */}
      <div className="rankings-section">
        <div className="ranking-box">
          <div className="chart-header">
            <h4><FaMedal className="icon-amber" /> Top 3 Income Units</h4>
            <span className="header-subtitle">Highest Revenue Generators</span>
          </div>
          <ul className="ranking-list">
            {topUnits.length > 0 ? topUnits.map((item, idx) => (
              <li key={idx} className="ranking-item">
                <span className="rank-badge gold">#{idx + 1}</span>
                <span className="rank-name">Unit: {item.name}</span>
                <span className="rank-value">{item.income.toLocaleString()} RWF</span>
              </li>
            )) : <p className="no-data">No unit income data available.</p>}
          </ul>
        </div>

        <div className="ranking-box">
          <div className="chart-header">
            <h4><FaMedal className="icon-rose" /> Least 3 Income Units</h4>
            <span className="header-subtitle">Opportunities for Optimization</span>
          </div>
          <ul className="ranking-list">
            {leastUnits.length > 0 ? leastUnits.map((item, idx) => (
              <li key={idx} className="ranking-item">
                <span className="rank-badge rose">#{idx + 1}</span>
                <span className="rank-name">Unit: {item.name}</span>
                <span className="rank-value">{item.income.toLocaleString()} RWF</span>
              </li>
            )) : <p className="no-data">No unit income data available.</p>}
          </ul>
        </div>
      </div>

      {/* Analytics Charts Section */}
      <div className="charts-section">
        <div className="chart-box">
          <div className="chart-header">
            <h4><FaChartBar /> Rent Distribution per Apartment Unit</h4>
            <span className="header-subtitle">Comparative revenue analysis across properties</span>
          </div>
          <div className="chart-wrapper">
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={barData} margin={{ top: 10, right: 30, left: 10, bottom: 50 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="name" interval={0} angle={-30} textAnchor="end" height={50} tick={{ fill: '#64748b', fontSize: 12 }} axisLine={{ stroke: '#e2e8f0' }} />
                <YAxis tick={{ fill: '#64748b', fontSize: 12 }} axisLine={{ stroke: '#e2e8f0' }} />
                <Tooltip contentStyle={{ backgroundColor: '#0f172a', color: '#fff', borderRadius: '10px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }} itemStyle={{ color: '#fff' }} />
                <Bar dataKey="Rent" fill="url(#colorGradient)" radius={[6, 6, 0, 0]} />
                <defs>
                  <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6366f1" />
                    <stop offset="100%" stopColor="#4f46e5" />
                  </linearGradient>
                </defs>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="chart-box">
          <div className="chart-header">
            <h4><FaBuilding /> Unit Status Distribution</h4>
            <span className="header-subtitle">Proportion of occupied versus available assets</span>
          </div>
          <div className="chart-wrapper">
            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={65}
                  outerRadius={105}
                  paddingAngle={6}
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} strokeWidth={2} stroke="#fff" />
                  ))}
                </Pie>
                <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: '13px', paddingTop: '10px' }} />
                <Tooltip contentStyle={{ backgroundColor: '#0f172a', color: '#fff', borderRadius: '10px', border: 'none' }} itemStyle={{ color: '#fff' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <style>{`
        .dashboard-home {
          padding: 36px;
          margin-left: 10%;
          background-color: #f8fafc;
          min-height: 100vh;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          box-sizing: border-box;
          width: calc(100% - 10%);
        }
        .dashboard-loader {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100vh;
          background-color: #f8fafc;
          color: #4b5563;
          font-family: 'Inter', sans-serif;
        }
        .spinner {
          width: 44px;
          height: 44px;
          border: 4px solid #e2e8f0;
          border-top: 4px solid #6366f1;
          border-radius: 50%;
          animation: spin 0.8s cubic-bezier(0.4, 0, 0.2, 1) infinite;
          margin-bottom: 16px;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .dashboard-header-flex {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 32px;
          background: #ffffff;
          padding: 28px 32px;
          border-radius: 20px;
          box-shadow: 0 4px 20px -2px rgba(0, 0, 0, 0.03);
          border: 1px solid #f1f5f9;
        }
        .badge-pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: #eef2ff;
          color: #6366f1;
          font-size: 11px;
          font-weight: 700;
          padding: 4px 10px;
          border-radius: 20px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 8px;
        }
        .dashboard-header h2 {
          font-size: 26px;
          font-weight: 800;
          color: #0f172a;
          margin: 0 0 6px 0;
          letter-spacing: -0.5px;
        }
        .dashboard-header p {
          color: #64748b;
          font-size: 14.5px;
          margin: 0;
        }
        .btn-download-pdf {
          display: flex;
          align-items: center;
          gap: 10px;
          background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
          color: #fff;
          border: none;
          padding: 12px 22px;
          border-radius: 12px;
          font-weight: 600;
          font-size: 14px;
          cursor: pointer;
          box-shadow: 0 4px 14px rgba(99, 102, 241, 0.35);
          transition: all 0.2s ease;
        }
        .btn-download-pdf:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(99, 102, 241, 0.45);
        }
        
        .dashboard-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 20px;
          margin-bottom: 24px;
        }
        .dashboard-card {
          background: #ffffff;
          padding: 18px 24px;
          border-radius: 16px;
          box-shadow: 0 4px 20px -2px rgba(0, 0, 0, 0.03);
          display: flex;
          align-items: center;
          justify-content: space-between;
          border: 1px solid #f1f5f9;
          transition: all 0.2s ease;
          min-height: 90px;
        }
        .dashboard-card:hover {
          transform: translateY(-2px);
        }
        .card-content h3 {
          font-size: 11.5px;
          text-transform: uppercase;
          letter-spacing: 0.6px;
          color: #64748b;
          margin: 0 0 4px 0;
          font-weight: 700;
        }
        .card-content p {
          font-size: 20px;
          font-weight: 800;
          color: #0f172a;
          margin: 0;
        }
        .icon-wrapper {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          flex-shrink: 0;
        }
        .card-blue .icon-wrapper { background: #eef2ff; color: #6366f1; }
        .card-indigo .icon-wrapper { background: #f0f4ff; color: #4f46e5; }
        .card-purple .icon-wrapper { background: #faf5ff; color: #9333ea; }
        .card-green .icon-wrapper { background: #ecfdf5; color: #059669; }
        .card-orange .icon-wrapper { background: #fff7ed; color: #ea580c; }
        .card-red .icon-wrapper { background: #fef2f2; color: #dc2626; }

        /* Financial Summary Bar (Non-Card Layout placed underneath cards) */
        .financial-summary-bar {
          background: #ffffff;
          padding: 20px 28px;
          border-radius: 16px;
          box-shadow: 0 4px 20px -2px rgba(0, 0, 0, 0.03);
          border: 1px solid #f1f5f9;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 16px;
          margin-bottom: 32px;
        }
        .financial-metric-item {
          display: flex;
          align-items: center;
          gap: 14px;
          flex: 1;
          min-width: 200px;
        }
        .financial-icon-small {
          width: 42px;
          height: 42px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          flex-shrink: 0;
        }
        .financial-icon-small.income { background: #ecfdf5; color: #059669; }
        .financial-icon-small.expenses { background: #fef2f2; color: #ef4444; }
        .financial-icon-small.profit { background: #eef2ff; color: #6366f1; }
        .financial-icon-small.average { background: #faf5ff; color: #9333ea; }
        
        .financial-text span {
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.6px;
          color: #64748b;
          font-weight: 700;
          display: block;
        }
        .financial-text h4 {
          font-size: 17px;
          font-weight: 800;
          color: #0f172a;
          margin: 2px 0 0 0;
        }
        .financial-divider {
          width: 1px;
          height: 36px;
          background-color: #f1f5f9;
        }
        .text-success { color: #059669 !important; }
        .text-danger { color: #ef4444 !important; }

        .rankings-section {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
          gap: 24px;
          margin-bottom: 32px;
        }
        .ranking-box {
          background: #ffffff;
          padding: 26px;
          border-radius: 20px;
          box-shadow: 0 4px 20px -2px rgba(0, 0, 0, 0.03);
          border: 1px solid #f1f5f9;
        }
        .ranking-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .ranking-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 0;
          border-bottom: 1px solid #f8fafc;
        }
        .ranking-item:last-child {
          border-bottom: none;
        }
        .rank-badge {
          font-weight: 800;
          font-size: 12px;
          padding: 6px 10px;
          border-radius: 8px;
        }
        .rank-badge.gold {
          background: #fef3c7;
          color: #d97706;
        }
        .rank-badge.rose {
          background: #ffe4e6;
          color: #e11d48;
        }
        .rank-name {
          font-weight: 600;
          color: #334155;
          flex: 1;
          margin-left: 14px;
          font-size: 14px;
        }
        .rank-value {
          font-weight: 700;
          color: #0f172a;
          font-size: 14.5px;
        }
        .no-data {
          color: #94a3b8;
          font-size: 14px;
          text-align: center;
          margin: 20px 0;
        }
        .charts-section {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(460px, 1fr));
          gap: 24px;
        }
        .chart-box {
          background: #ffffff;
          padding: 26px;
          border-radius: 20px;
          box-shadow: 0 4px 20px -2px rgba(0, 0, 0, 0.03);
          border: 1px solid #f1f5f9;
        }
        .chart-header {
          margin-bottom: 20px;
        }
        .chart-header h4 {
          font-size: 16.5px;
          font-weight: 700;
          color: #0f172a;
          margin: 0 0 4px 0;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .header-subtitle {
          font-size: 12.5px;
          color: #64748b;
          font-weight: 500;
        }
        .icon-amber { color: #f59e0b; }
        .icon-rose { color: #e11d48; }
        .chart-wrapper {
          width: 100%;
          min-height: 320px;
        }
        @media (max-width: 1024px) {
          .dashboard-home {
            margin-left: 0;
            width: 100%;
            padding: 20px;
          }
          .dashboard-header-flex {
            flex-direction: column;
            gap: 16px;
            align-items: stretch;
          }
          .btn-download-pdf {
            width: 100%;
            justify-content: center;
          }
          .financial-summary-bar {
            flex-direction: column;
            align-items: flex-start;
          }
          .financial-divider {
            display: none;
          }
          .financial-metric-item {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default DashboardHome;